import React, { useMemo, useState } from 'react';

import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSnackbar } from 'notistack';
import { Control, useController } from 'react-hook-form';

import AcUnitIcon from '@mui/icons-material/AcUnit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccessibleIcon from '@mui/icons-material/Accessible';
import BedIcon from '@mui/icons-material/Bed';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DownhillSkiingIcon from '@mui/icons-material/DownhillSkiing';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ForestIcon from '@mui/icons-material/Forest';
import GolfCourseIcon from '@mui/icons-material/GolfCourse';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PoolIcon from '@mui/icons-material/Pool';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SpaIcon from '@mui/icons-material/Spa';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import WavesIcon from '@mui/icons-material/Waves';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import WifiIcon from '@mui/icons-material/Wifi';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import type { Offer as OfferContract } from '@luxuryescapes/contract-svc-offer';

import { useUpdateOffer } from '~/hooks/apis/offers/useGetOffer';

type OfferPageHighlightTag = OfferContract.OfferPageHighlightTag;

interface Props {
  control: Control<any>;
  offerId?: string;
}

const MAX_SELECTED_TAGS = 4;

const TAG_LIBRARY: Array<OfferPageHighlightTag> = [
  {
    id: 'maldives-charter',
    icon: 'plane',
    title: 'Maldives charter',
    description: 'Exclusive for Luxury Escapes guests',
  },
  {
    id: 'all-inclusive',
    icon: 'utensils',
    title: 'All-inclusive',
    description: 'Food, drinks and more, all taken care of',
  },
  { id: 'breakfast-included', icon: 'cloch', title: 'Breakfast included', description: 'Start your day the right way' },
  { id: 'adults-only', icon: 'sunset', title: 'Adults only', description: 'A retreat just for grown-ups' },
  {
    id: 'adults-only-area',
    icon: 'sunset',
    title: 'Adults-Only Area',
    description: 'Peaceful spaces reserved for adults',
  },
  {
    id: 'massage-included',
    icon: 'wellness',
    title: 'Massage included',
    description: 'A little extra relaxation, included',
  },
  { id: 'kids-stay-free', icon: 'kid', title: 'Kids stay free', description: 'Extra value for family stays' },
  { id: 'dining-credit', icon: 'utensils', title: 'Dining credit', description: 'Spend it on delicious moments' },
  { id: 'daily-cocktails', icon: 'glass-martini', title: 'Daily cocktails', description: 'Raise a glass each day' },
  {
    id: 'nightly-drink',
    icon: 'glass-martini',
    title: 'Nightly drink',
    description: 'Settle in with a drink each night',
  },
  {
    id: 'late-checkout',
    icon: 'clock',
    title: 'Late checkout',
    description: 'Enjoy a little more time before you leave',
  },
  {
    id: 'great-for-couples',
    icon: 'heart-card',
    title: 'Great for couples',
    description: 'Made for a romantic escape',
  },
  { id: 'family-friendly', icon: 'family', title: 'Family friendly', description: 'Designed with families in mind' },
  { id: 'highly-rated', icon: 'star', title: 'Highly rated by travellers', description: 'Trusted and loved by guests' },
  {
    id: 'ski-in-ski-out',
    icon: 'ski-lift',
    title: 'Ski-in/ski-out',
    description: 'Hit the slopes right from your door',
  },
  { id: 'beach-nearby', icon: 'water', title: 'Beach nearby', description: 'Sand and sea within easy reach' },
  {
    id: 'atlas-beach-club',
    icon: 'water',
    title: 'Atlas Beach Club',
    description: 'Access to the world’s largest beach club',
  },
  {
    id: 'canna-bali-beach-club-access',
    icon: 'water',
    title: 'CANNA Bali Beach Club Access',
    description: 'Bali’s hottest beach club, included',
  },
  {
    id: 'central-location',
    icon: 'map-marker',
    title: 'Central location',
    description: 'Right in the heart of it all',
  },
  { id: 'great-location', icon: 'map-marker', title: 'Great location', description: 'Everything on your doorstep' },
  {
    id: 'walkable-neighbourhood',
    icon: 'walking',
    title: 'Walkable neighbourhood',
    description: 'Explore the area on foot',
  },
  {
    id: 'arts-culture-nearby',
    icon: 'theatre',
    title: 'Arts & culture nearby',
    description: 'Museums, galleries and more on your doorstep',
  },
  {
    id: 'return-airport-transfers',
    icon: 'plane',
    title: 'Return airport transfers',
    description: 'Arrive and depart with ease',
  },
  { id: 'golf-nearby', icon: 'golf', title: 'Golf nearby', description: 'Tee off without going far' },
  {
    id: 'easy-airport-access',
    icon: 'plane',
    title: 'Easy airport access',
    description: 'Arrive and depart with ease',
  },
  { id: 'close-to-transport', icon: 'car', title: 'Close to transport', description: 'Easy to get around' },
  { id: 'ocean-view-rooms', icon: 'water', title: 'Ocean-view rooms', description: 'Wake up to water views' },
  { id: 'spacious-rooms', icon: 'bed-sofa', title: 'Spacious rooms', description: 'Extra space to relax' },
  {
    id: 'interconnecting-rooms',
    icon: 'bed-sofa',
    title: 'Interconnecting rooms',
    description: 'Perfect for families or groups travelling together',
  },
  { id: 'free-wifi', icon: 'wifi', title: 'Free WiFi', description: 'Stay connected throughout your stay' },
  { id: 'reception', icon: 'clock', title: '24-hour reception', description: 'Support any time, day or night' },
  { id: 'express-check-in', icon: 'clock', title: 'Express check-in', description: 'Get settled faster on arrival' },
  { id: 'parking', icon: 'parking', title: 'Parking', description: 'Make the most of onsite parking' },
  {
    id: 'airport-transfers',
    icon: 'plane',
    title: 'Airport transfers',
    description: 'Straight from the airport to your door',
  },
  { id: 'onsite-dining', icon: 'restaurant', title: 'Onsite dining', description: 'Restaurant and dining onsite' },
  {
    id: 'bar-lounge',
    icon: 'glass-martini',
    title: 'Bar & lounge onsite',
    description: 'Settle in and stay for a drink',
  },
  { id: 'room-service', icon: 'cloch', title: 'Room service', description: 'Eat in the comfort of your room' },
  { id: 'indoor-pool', icon: 'swimmer', title: 'Indoor pool', description: 'Swim year-round in comfort' },
  { id: 'kids-pool', icon: 'kid', title: 'Kids pool', description: 'Water fun for kids of all ages' },
  { id: 'fitness-centre', icon: 'dumbbell', title: 'Fitness centre', description: 'For those who crave a workout' },
  {
    id: 'spa-wellness',
    icon: 'wellness',
    title: 'Spa & wellness facilities',
    description: 'Relax and recharge onsite',
  },
  {
    id: 'accessible-facilities',
    icon: 'wheelchair',
    title: 'Accessible facilities',
    description: 'Built with accessibility in mind',
  },
  {
    id: 'family-pools',
    icon: 'water',
    title: 'Family pools and activities',
    description: 'Kids can splash and stay entertained',
  },
  {
    id: 'family-location',
    icon: 'trees',
    title: 'Close to beaches and fun',
    description: 'Easy family day trips nearby',
  },
  {
    id: 'family-dining',
    icon: 'restaurant',
    title: 'Dining for every taste',
    description: 'Options for even picky eaters',
  },
  {
    id: 'family-space',
    icon: 'bed-sofa',
    title: 'Space for families to relax',
    description: 'Pools, gardens and open areas',
  },
  {
    id: 'couple-sunset',
    icon: 'sunset',
    title: 'Sunset views together',
    description: 'Watch the sun dip below the horizon',
  },
  {
    id: 'couple-cocktails',
    icon: 'glass-martini',
    title: 'Cocktails and relaxed nights',
    description: 'Enjoy drinks and laid-back evenings',
  },
  {
    id: 'couple-resort',
    icon: 'trees',
    title: 'Peaceful resort atmosphere',
    description: 'Quiet spaces to relax and reconnect',
  },
  {
    id: 'couple-service',
    icon: 'star',
    title: 'Highly rated for service',
    description: 'Guests praise the warm hospitality',
  },
  {
    id: 'default-rating',
    icon: 'star',
    title: 'Highly rated by travellers',
    description: 'Loved for its service and dining',
  },
  {
    id: 'default-location',
    icon: 'map-marker',
    title: 'Great location to explore',
    description: 'Close to beaches and restaurants',
  },
  {
    id: 'default-perks',
    icon: 'utensils',
    title: 'Premium perks included',
    description: 'Enjoy curated extras and drinks',
  },
  {
    id: 'default-setting',
    icon: 'trees',
    title: 'Beautiful resort setting',
    description: 'Pools, gardens and ocean views',
  },
];

function HighlightTagIcon({ icon }: { icon: string }) {
  const commonProps = { fontSize: 'small' as const };

  switch (icon) {
    case 'bed-sofa':
      return <BedIcon {...commonProps} />;
    case 'car':
      return <DirectionsCarIcon {...commonProps} />;
    case 'cloch':
      return <RoomServiceIcon {...commonProps} />;
    case 'clock':
      return <AccessTimeIcon {...commonProps} />;
    case 'dumbbell':
      return <FitnessCenterIcon {...commonProps} />;
    case 'family':
      return <GroupsIcon {...commonProps} />;
    case 'glass-martini':
      return <LocalBarIcon {...commonProps} />;
    case 'golf':
      return <GolfCourseIcon {...commonProps} />;
    case 'grapes':
      return <LocalDiningIcon {...commonProps} />;
    case 'heart-card':
      return <FavoriteBorderIcon {...commonProps} />;
    case 'kid':
      return <ChildCareIcon {...commonProps} />;
    case 'map-marker':
      return <LocationOnIcon {...commonProps} />;
    case 'parking':
      return <LocalParkingIcon {...commonProps} />;
    case 'plane':
      return <FlightTakeoffIcon {...commonProps} />;
    case 'restaurant':
      return <RestaurantIcon {...commonProps} />;
    case 'shop':
      return <ShoppingBagIcon {...commonProps} />;
    case 'ski-lift':
      return <DownhillSkiingIcon {...commonProps} />;
    case 'snow-flake':
      return <AcUnitIcon {...commonProps} />;
    case 'star':
      return <StarBorderIcon {...commonProps} />;
    case 'sunset':
      return <WbTwilightIcon {...commonProps} />;
    case 'swimmer':
      return <PoolIcon {...commonProps} />;
    case 'theatre':
      return <TheaterComedyIcon {...commonProps} />;
    case 'trees':
      return <ForestIcon {...commonProps} />;
    case 'utensils':
      return <LocalDiningIcon {...commonProps} />;
    case 'walking':
      return <DirectionsWalkIcon {...commonProps} />;
    case 'water':
      return <WavesIcon {...commonProps} />;
    case 'wellness':
      return <SpaIcon {...commonProps} />;
    case 'wheelchair':
      return <AccessibleIcon {...commonProps} />;
    case 'wifi':
      return <WifiIcon {...commonProps} />;
    default:
      return <StarBorderIcon {...commonProps} />;
  }
}

function normalizeTags(value: unknown): Array<OfferPageHighlightTag> {
  return Array.isArray(value) ? value : [];
}

function SortableSelectedTag({
  index,
  tag,
  onRemove,
}: {
  index: number;
  tag: OfferPageHighlightTag;
  onRemove: (tagId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tag.id });

  return (
    <Paper
      {...attributes}
      {...listeners}
      ref={setNodeRef}
      variant="outlined"
      sx={{
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.72 : 1,
        position: 'relative',
        zIndex: isDragging ? 10 : 'auto',
        boxShadow: isDragging ? 4 : 'none',
      }}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ width: 20, textAlign: 'right' }}>
          {index + 1}
        </Typography>
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>
          <HighlightTagIcon icon={tag.icon} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {tag.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {tag.description}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Remove highlight tag"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => onRemove(tag.id)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}

function CompactHighlightTag({ tag }: { tag: OfferPageHighlightTag }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        width: 222,
        minHeight: 78,
        p: 1.25,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'grey.100',
            borderRadius: '50%',
            color: 'text.primary',
            display: 'flex',
            flexShrink: 0,
            height: 48,
            justifyContent: 'center',
            width: 48,
            '& .MuiSvgIcon-root': {
              fontSize: 30,
            },
          }}
        >
          <HighlightTagIcon icon={tag.icon} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} display="block" noWrap>
            {tag.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>
            {tag.description}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function OfferPageHighlightTagsOverride({ control, offerId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const updateOfferMutation = useUpdateOffer(offerId ?? '');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draftTags, setDraftTags] = useState<Array<OfferPageHighlightTag>>([]);

  const {
    field: { value, onChange },
  } = useController({
    control,
    name: 'offer_page_highlight_tags_override',
  });

  const selectedTags = normalizeTags(value);
  const preventTags = Array.isArray(value) && value.length === 0;
  const selectedTagIds = useMemo(() => new Set(draftTags.map((tag) => tag.id)), [draftTags]);
  const filteredLibrary = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return TAG_LIBRARY.filter((tag) => {
      if (selectedTagIds.has(tag.id)) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return (
        tag.title.toLowerCase().includes(normalizedSearch) || tag.description.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [search, selectedTagIds]);

  const openDrawer = () => {
    setDraftTags(selectedTags);
    setSearch('');
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const removeDraftTag = (tagId: string) => {
    setDraftTags((current) => current.filter((tag) => tag.id !== tagId));
  };

  const addDraftTag = (tag: OfferPageHighlightTag) => {
    setDraftTags((current) => (current.some((selectedTag) => selectedTag.id === tag.id) ? current : [...current, tag]));
  };

  const handleDraftDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = draftTags.findIndex((tag) => tag.id === active.id);
    const newIndex = draftTags.findIndex((tag) => tag.id === over.id);
    setDraftTags(arrayMove(draftTags, oldIndex, newIndex));
  };

  const saveDraftTags = async () => {
    if (draftTags.length > MAX_SELECTED_TAGS) {
      return;
    }

    const saved = await saveOverride(draftTags.length === 0 ? null : draftTags);

    if (saved) {
      setDrawerOpen(false);
    }
  };

  const saveOverride = async (nextTags: Array<OfferPageHighlightTag> | null) => {
    if (!offerId) {
      enqueueSnackbar('Cannot save highlight tags: missing offer id', { variant: 'error' });
      return false;
    }

    try {
      await updateOfferMutation.mutateAsync({ offer_page_highlight_tags_override: nextTags });
      onChange(nextTags);
      setDraftTags(nextTags ?? []);
      enqueueSnackbar('Highlight tags saved successfully', { variant: 'success' });
      return true;
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save highlight tags', { variant: 'error' });
      return false;
    }
  };

  const isSaving = updateOfferMutation.isPending;
  const tooManyTags = draftTags.length > MAX_SELECTED_TAGS;

  const handlePreventTagsChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    void saveOverride(checked ? [] : null);
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" fontWeight={600}>
            Offer page highlight tags
          </Typography>
          {selectedTags.length > 0 ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {selectedTags.map((tag) => (
                <CompactHighlightTag key={tag.id} tag={tag} />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No override set. The offer page will use system-recommended tags.
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary">
            {preventTags
              ? 'Tags are prevented from being shown on the offer page.'
              : 'These tags are shown on the offer page.'}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <FormControlLabel
              control={
                <Switch
                  checked={preventTags}
                  onChange={handlePreventTagsChange}
                  disabled={isSaving}
                  inputProps={{ 'aria-label': 'Prevent tags from being shown on a hotel' }}
                />
              }
              label="Prevent tags from being shown on a hotel"
            />
            <Button variant="contained" size="small" onClick={openDrawer} disabled={isSaving || preventTags}>
              Edit tags
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
      >
        <Stack sx={{ height: '100%' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
            <Typography variant="h6">Edit highlight tags</Typography>
            <IconButton aria-label="Close highlight tags drawer" onClick={closeDrawer}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Typography variant="subtitle2">Selected tag order</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Drag to reorder. Save up to {MAX_SELECTED_TAGS} tags.
            </Typography>

            {draftTags.length > 0 ? (
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDraftDragEnd}>
                <SortableContext items={draftTags.map((tag) => tag.id)} strategy={verticalListSortingStrategy}>
                  <Stack spacing={1}>
                    {draftTags.map((tag, index) => (
                      <SortableSelectedTag key={tag.id} index={index} tag={tag} onRemove={removeDraftTag} />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            ) : (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No tags selected.
                </Typography>
              </Paper>
            )}

            {tooManyTags && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Will not save until {MAX_SELECTED_TAGS} or fewer tags.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Add another tag
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tags"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />

            <Stack spacing={1}>
              {filteredLibrary.map((tag) => (
                <Paper key={tag.id} variant="outlined" sx={{ p: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                      <HighlightTagIcon icon={tag.icon} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {tag.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {tag.description}
                      </Typography>
                    </Box>
                    <Button size="small" onClick={() => addDraftTag(tag)}>
                      Add
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>

          <Divider />
          <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ p: 2 }}>
            <Button variant="outlined" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button variant="contained" onClick={saveDraftTags} disabled={tooManyTags || isSaving}>
              {isSaving ? 'Saving...' : 'Save tags'}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
}
